/**
 * JavaScript bridge for our custom Kotlin Health Connect plugin.
 *
 * Replaces @capgo/capacitor-health on Android. The Capgo plugin has a
 * documented bug on Android 14+ where requestAuthorization() returns
 * `denied` synchronously without showing the picker UI. Our own Kotlin
 * plugin uses androidx.health.connect.client.PermissionController
 * directly — same code path that Google's docs recommend — and the
 * picker actually appears.
 *
 * The Kotlin side lives at
 *   android/app/src/main/java/com/fitjong/app/HealthConnectPlugin.kt
 * and is regenerated on every `yarn install` / `yarn cap:sync` via
 * scripts/install-custom-health-plugin.js (idempotent).
 *
 * Exposed methods:
 *   isAvailable() → { available, sdkStatus }
 *   checkHealthPermissions() → { granted, grantedPerms[] }  (no UI — just reads grant state)
 *   requestPermissions() → { granted, grantedPerms[] }
 *   readStepsToday() → { steps }
 *   openSettings() → void
 *
 * On non-Android (iOS, web), these are no-ops that return { ok: false }.
 */
import { registerPlugin, Capacitor } from "@capacitor/core";

const HCN = registerPlugin("HealthConnectNative");

function isAndroid() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

export async function hcnIsAvailable() {
  if (!isAndroid()) return { available: false, sdkStatus: -1 };
  try {
    return await HCN.isAvailable();
  } catch (e) {
    return { available: false, sdkStatus: -1, error: String(e?.message || e) };
  }
}

/**
 * Read the CURRENT permission-grant state without launching any UI.
 * Uses the canonical PermissionController.getGrantedPermissions() on the
 * native side. Returns `{ granted: bool, grantedPerms[] }`. On an older
 * build that predates this method (or if HC is unavailable) it returns
 * `{ granted: false, unsupported: true }` so callers can fall back.
 */
export async function hcnCheckPermissions() {
  if (!isAndroid()) return { granted: false, reason: "non-android" };
  try {
    return await HCN.checkHealthPermissions();
  } catch (e) {
    return { granted: false, unsupported: true, error: String(e?.message || e) };
  }
}

export async function hcnRequestPermissions() {
  if (!isAndroid()) return { granted: false, reason: "non-android" };
  try {
    return await HCN.requestHealthPermissions();
  } catch (e) {
    return { granted: false, reason: "error", error: String(e?.message || e) };
  }
}

export async function hcnReadStepsToday() {
  if (!isAndroid()) return { ok: false, steps: 0, reason: "non-android" };
  try {
    const { steps } = await HCN.readStepsToday();
    return { ok: true, steps: Number(steps) || 0 };
  } catch (e) {
    return { ok: false, steps: 0, reason: "error", error: String(e?.message || e) };
  }
}

export async function hcnOpenSettings() {
  if (!isAndroid()) return { ok: false, reason: "non-android" };
  try {
    await HCN.openSettings();
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "error", error: String(e?.message || e) };
  }
}
